import React, { useMemo } from 'react';
import {
  EmptyState,
  EMPTY_STATE_SIZE_XS,
  StatTile,
  Sparkline,
  type SparklineBin,
} from '@aibuild/ui';
import {
  StatsCard,
  StatsRow,
  SparkSection,
  SparkLabel,
} from './RunHistoryAnalytics.styled';

export interface RunHistoryStats {
  totalRuns: number;
  successRate: number;
  medianDurationMs: number;
  last14Days: number[];
}

export interface RunHistoryAnalyticsProps {
  stats: RunHistoryStats | null;
  dataTestId?: string;
}

const fmtDuration = (ms: number): string => {
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s}s`;
};

const RunHistoryAnalytics: React.FC<RunHistoryAnalyticsProps> = ({
  stats,
  dataTestId = 'run-history-analytics',
}) => {
  const bins: SparklineBin[] = useMemo(() => {
    if (!stats?.last14Days) return [];
    return stats.last14Days.map((v, i) => ({
      value: v,
      label: `Day ${i + 1}`,
    }));
  }, [stats]);

  if (!stats || stats.totalRuns === 0) {
    return (
      <StatsCard $empty data-testid={dataTestId} aria-label="Run history">
        <EmptyState
          size={EMPTY_STATE_SIZE_XS}
          title="No runs on this process yet."
          dataTestId={`${dataTestId}__empty`}
        />
      </StatsCard>
    );
  }

  return (
    <StatsCard data-testid={dataTestId} aria-label="Run history">
      <StatsRow>
        <StatTile
          value={stats.totalRuns}
          label="total runs"
          align="center"
          size="sm"
          labelTransform="none"
          dataTestId={`${dataTestId}__total`}
        />
        <StatTile
          value={`${Math.round(stats.successRate)}%`}
          label="success rate"
          tone="success"
          align="center"
          size="sm"
          labelTransform="none"
          dataTestId={`${dataTestId}__success`}
        />
        <StatTile
          value={fmtDuration(stats.medianDurationMs)}
          label="median duration"
          align="center"
          size="sm"
          labelTransform="none"
          dataTestId={`${dataTestId}__median`}
        />
      </StatsRow>

      {bins.length > 0 && (
        <SparkSection>
          <SparkLabel>Last 14 days</SparkLabel>
          <Sparkline
            bins={bins}
            height={32}
            tone="default"
            interactive={false}
            ariaLabel="Runs per day, last 14 days"
            formatValue={(n) => `${n} runs`}
            dataTestId={`${dataTestId}__sparkline`}
          />
        </SparkSection>
      )}
    </StatsCard>
  );
};

export default RunHistoryAnalytics;
